import type { Product } from 'services/generated/commerce-service/models/Product';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import type { AccessoryCategory } from 'services/generated/commerce-service/models/AccessoryCategory';
import type { ShopAccessoryCategory } from 'services/generated/commerce-service/models/ShopAccessoryCategory';
import {
  getInseamLabel,
  getPriceValues,
  getSizeLabel,
  hasInseamAttributes,
  hasSizeAttributes,
  inseamVariantSorter,
  sizeVariantSorter,
} from './variantHelpers';

export const getFirstProductWithVariantInStock = (
  products: Product[],
): Product => {
  return (
    products.find((product) =>
      product.variants.some((variant) => variant.in_stock),
    ) ?? products[0]
  );
};

export const getFirstVariantInStock = (product: Product): Variant => {
  return (
    product.variants.find((variant) => variant.in_stock) ??
    getFirstVariant(product)
  );
};

export const getFirstVariant = (product: Product): Variant => {
  return product?.variants[0];
};

export const getRandomVariant = (products: Product[]) => {
  const allVariants = products
    .flatMap((product) => product.variants)
    .filter((variant) => variant.in_stock);
  return allVariants[Math.floor(Math.random() * allVariants.length)];
};

export const getAllProductsFromCategory = (
  category: AccessoryCategory | ShopAccessoryCategory,
): Product[] => {
  if (category.products.length !== 0) {
    return category.products;
  }

  const productList: Product[] = [];
  category.categories.forEach((cat) => productList.push(...cat.products));
  return productList;
};

export const getIsPackProduct = (category: AccessoryCategory) => {
  const products = getAllProductsFromCategory(category);
  return products[0]?.product_type === 'pack';
};

export const getIsMCB = (category: AccessoryCategory) => {
  const products = getAllProductsFromCategory(category);
  return products[0]?.product_type === 'milestone-collectors-box';
};

export const getIsWYWProduct = (category: AccessoryCategory) => {
  const products = getAllProductsFromCategory(category);
  return products[0]?.product_type === 'whoop-your-way';
};

export const getChildPackProductsFromCatalog = (
  accessories: AccessoryCategory[] | ShopAccessoryCategory[],
  pack: AccessoryCategory | ShopAccessoryCategory,
): AccessoryCategory[] => {
  const packProduct = getFirstVariant(pack.products[0]);
  const packProductsWithDetails: AccessoryCategory[] = [];
  packProduct?.attributes?.forEach((attribute) => {
    const catalogProduct: AccessoryCategory = findSubCategory(
      attribute.value.id,
      // Creating 1 fake category to hold the entire category tree since this helper
      // requires a category as input, not a list
      {
        category_id: 'id',
        categories: [...accessories],
      } as AccessoryCategory,
    );
    if (catalogProduct) packProductsWithDetails.push(catalogProduct);
  });
  return packProductsWithDetails;
};

export const getDefaultVariantsFromProducts = (
  productList: Product[],
): Variant[] => {
  return productList.map((product) => getFirstVariant(product));
};

export const getColorVariantsWithSameAttributes = (
  productList: Product[],
  selectedVariant: Variant,
): Variant[] => {
  if (hasInseamAttributes(selectedVariant)) {
    return productList.map((product) => {
      return (
        product.variants.find((variant) => {
          const currVarSize = getSizeLabel(variant);
          const selectedVarSize = getSizeLabel(selectedVariant);
          const currVarInseam = getInseamLabel(variant);
          const selectedVarInseam = getInseamLabel(selectedVariant);
          return (
            currVarSize === selectedVarSize &&
            currVarInseam === selectedVarInseam
          );
        }) ?? ({} as Variant)
      );
    });
  }
  if (hasSizeAttributes(selectedVariant)) {
    return productList.map((product) => {
      return (
        product.variants.find((variant) => {
          const currVarSize = getSizeLabel(variant);
          const selectedVarSize = getSizeLabel(selectedVariant);
          return currVarSize === selectedVarSize;
        }) ?? ({} as Variant)
      );
    });
  }
  return getDefaultVariantsFromProducts(productList);
};

export const getSizeVariantsWithSameAttributes = (
  selectedProduct: Product,
  selectedVariant: Variant,
): Variant[] => {
  if (!hasSizeAttributes(selectedVariant)) return [];
  if (!hasInseamAttributes(selectedVariant)) {
    return [...selectedProduct.variants].sort(sizeVariantSorter);
  }
  return selectedProduct.variants
    .filter(
      (variant) => getInseamLabel(variant) === getInseamLabel(selectedVariant),
    )
    .sort(sizeVariantSorter);
};

export const getInseamVariantsWithSameAttributes = (
  selectedProduct: Product,
  selectedVariant: Variant,
): Variant[] => {
  if (!hasInseamAttributes(selectedVariant)) return [];
  return selectedProduct.variants
    .filter(
      (variant) => getSizeLabel(variant) === getSizeLabel(selectedVariant),
    )
    .sort(inseamVariantSorter);
};

export const collapsePrices = (products: Product[]): Set<number> => {
  return new Set(
    products.flatMap((product) =>
      product.variants.map(
        (variant) => getPriceValues(variant).originalPrice ?? 0,
      ),
    ),
  );
};

export const findSubCategory = (id: string, category: AccessoryCategory) => {
  if (id === category.category_id) {
    return category;
  }
  let subCategory;
  category.categories.forEach((cat) => {
    const result = findSubCategory(id, cat);
    if (result !== undefined) {
      subCategory = result;
    }
  });
  return subCategory;
};

export function getVariantBySku({
  sku,
  products,
  categories,
}: {
  sku: string | null;
  products: Product[];
  categories: AccessoryCategory[] | ShopAccessoryCategory[];
}): Variant | undefined {
  const variantsFromProducts = products.flatMap((product) => product.variants);
  const variantsFromCategories = categories.flatMap((category) =>
    category.products.flatMap((product) => product.variants),
  );

  const allVariants = [...variantsFromProducts, ...variantsFromCategories];
  return allVariants.find((variant) => variant.sku === sku);
}
